import type { WorkerReply, RenderedPagePayload } from "./pdf.protocol";
export { SLIDE_IMAGE_TYPE } from "./pdf.protocol";

export const PDF_MAX_BYTES = 50 * 1024 * 1024; // Interactions API inline-ingest limit

export type PdfValidation = { ok: true } | { ok: false; reason: string };

/** True iff the bytes begin with the PDF magic header "%PDF-". */
export function checkPdfMagic(bytes: Uint8Array): boolean {
  const magic = [0x25, 0x50, 0x44, 0x46, 0x2d]; // %PDF-
  if (bytes.length < magic.length) return false;
  return magic.every((b, i) => bytes[i] === b);
}

export function validateSize(byteLength: number): PdfValidation {
  if (byteLength >= PDF_MAX_BYTES) {
    return { ok: false, reason: "That PDF is over 50MB — too big to roast. Try a smaller export." };
  }
  return { ok: true };
}

export const PDF_MAX_PAGES = 200;

let _worker: WorkerLike | null = null;
/** Lazy real-Worker singleton (module worker). Injectable override in tests. */
export function getPdfWorker(): WorkerLike {
  _worker ??= new Worker(new URL("./pdf.worker.ts", import.meta.url), { type: "module" }) as unknown as WorkerLike;
  return _worker;
}

export async function getPageCount(file: Blob, worker: WorkerLike = getPdfWorker()): Promise<number> {
  const pdf = await file.arrayBuffer();
  return runCount(worker, pdf, PDF_WORKER_TIMEOUT_MS);
}

export async function validatePdf(
  file: Blob, worker: WorkerLike = getPdfWorker(),
): Promise<PdfValidation & { pageCount?: number }> {
  const head = new Uint8Array(await file.slice(0, 8).arrayBuffer());
  if (!checkPdfMagic(head)) return { ok: false, reason: "That doesn't look like a PDF." };
  const size = validateSize(file.size);
  if (!size.ok) return size;
  const pageCount = await getPageCount(file, worker);
  if (pageCount > PDF_MAX_PAGES) {
    return { ok: false, reason: `That deck has ${pageCount} pages — keep it under ${PDF_MAX_PAGES}.` };
  }
  return { ok: true, pageCount };
}

// ─── Client request core ──────────────────────────────────────────────────────

export interface WorkerLike {
  postMessage(message: unknown, transfer?: Transferable[]): void;
  addEventListener(type: "message" | "error", listener: (ev: any) => void): void;
  removeEventListener(type: "message" | "error", listener: (ev: any) => void): void;
}

export const PDF_WORKER_TIMEOUT_MS = 30_000;

let _seq = 0;

/**
 * Send one id-tracked request and settle from this id's replies. `handle` inspects
 * each matching reply and returns { done } to resolve, { error } to reject, or
 * undefined to keep waiting. Rejects on a worker "error" event or after timeoutMs.
 * Always removes its listeners and clears the timer.
 */
function dispatch<T>(
  worker: WorkerLike,
  message: Record<string, unknown>,
  transfer: Transferable[],
  timeoutMs: number,
  handle: (reply: WorkerReply) => { done: T } | { error: string } | undefined,
): Promise<T> {
  const id = ++_seq;
  return new Promise<T>((resolve, reject) => {
    let settled = false;
    const cleanup = () => {
      settled = true;
      worker.removeEventListener("message", onMsg);
      worker.removeEventListener("error", onErr);
      clearTimeout(timer);
    };
    const timer = setTimeout(() => {
      if (settled) return;
      cleanup();
      reject(new Error("pdf worker timed out"));
    }, timeoutMs);
    const onMsg = (ev: { data?: WorkerReply }) => {
      const reply = ev.data;
      if (settled || !reply || reply.id !== id) return;
      const r = handle(reply);
      if (!r) return;
      cleanup();
      if ("error" in r) reject(new Error(r.error));
      else resolve(r.done);
    };
    const onErr = (ev: { message?: string }) => {
      if (settled) return;
      cleanup();
      reject(new Error(`pdf worker crashed: ${ev?.message ?? "unknown"}`));
    };
    worker.addEventListener("message", onMsg);
    worker.addEventListener("error", onErr);
    worker.postMessage({ ...message, id }, transfer);
  });
}

/** Reorder streamed pages to match `indices`; drop any index with no page. */
export function orderPages(
  pages: RenderedPagePayload[], indices: number[],
): RenderedPagePayload[] {
  const byIndex = new Map(pages.map((p) => [p.slideIndex, p]));
  return indices.map((i) => byIndex.get(i)).filter((p): p is RenderedPagePayload => !!p);
}

export function runCount(worker: WorkerLike, pdf: ArrayBuffer, timeoutMs: number): Promise<number> {
  return dispatch<number>(worker, { kind: "count", pdf }, [pdf], timeoutMs, (reply) =>
    reply.kind === "count" ? { done: reply.pageCount }
      : reply.kind === "error" ? { error: reply.message }
      : undefined,
  );
}

export function runBatch(
  worker: WorkerLike,
  args: { pdf: ArrayBuffer; indices: number[]; scale: number },
  opts: { timeoutMs: number; onPage?: (page: RenderedPagePayload, done: number, total: number) => void },
): Promise<RenderedPagePayload[]> {
  const collected: RenderedPagePayload[] = [];
  return dispatch<RenderedPagePayload[]>(
    worker,
    { kind: "render", pdf: args.pdf, indices: args.indices, scale: args.scale },
    [args.pdf],
    opts.timeoutMs,
    (reply) => {
      if (reply.kind === "page") {
        collected.push(reply.page);
        opts.onPage?.(reply.page, collected.length, args.indices.length);
        return undefined;
      }
      if (reply.kind === "done") return { done: orderPages(collected, args.indices) };
      if (reply.kind === "error") return { error: reply.message };
      return undefined;
    },
  );
}

// ─── Render section ───────────────────────────────────────────────────────────

export type RenderedPage = RenderedPagePayload;
export const KEY_SLIDE_SCALE = 2.0;
export const FILLER_SCALE = 1.0;
export const COVER_SCALE = 1.0;

export async function renderPages(
  file: Blob,
  indices: number[],
  scale: number,
  opts: { worker?: WorkerLike; onPage?: (page: RenderedPage, done: number, total: number) => void } = {},
): Promise<RenderedPage[]> {
  const worker = opts.worker ?? getPdfWorker();
  const pdf = await file.arrayBuffer();
  return runBatch(worker, { pdf, indices, scale }, { timeoutMs: PDF_WORKER_TIMEOUT_MS, onPage: opts.onPage });
}

export async function renderCover(
  file: Blob,
  opts: { worker?: WorkerLike } = {},
): Promise<RenderedPage> {
  return (await renderPages(file, [0], COVER_SCALE, opts))[0];
}
